import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { AuthContext } from '../shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🆕 مجمع التخفيضات / المصاريف — Aggregated Cashbox Expenses Log
 * Surfaces every approved general_expense row (refunds + granted discounts
 * + manager-issued cashbox expenses + airline payments + …) in one place
 * with KPIs and filters so the manager can audit "where did the cashbox
 * money go?" in seconds.
 */

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

// Arabic labels for the 8-9 internal expense_type values that surface
// from general_expenses (refunds, granted credit notes, airline payments,
// cashbox-only adjustments, etc.).
const EXPENSE_TYPE_LABELS = {
  refund: '↩️ استرجاع',
  discount_granted: '💸 سند تخفيض',
  airline_payment: '🛫 دفع لشركة طيران',
  supplier_payment: '🏪 دفع لمورد',
  tickets_payment: '✈️ تسديد تذاكر',
  other_expense: '📦 مصروف عام',
  rent: '🏢 إيجار',
  salary: '💼 رواتب',
  utilities: '⚡ مرافق',
  marketing: '📣 تسويق',
};

const labelForType = (t) => EXPENSE_TYPE_LABELS[t] || `🔸 ${t || '—'}`;

const ExpenseAggregator = () => {
  const { user } = useContext(AuthContext);
  const isAuthorized = user?.role === 'super_admin' || user?.role === 'general_accountant';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    start_date: monthAgo(),
    end_date: today(),
    expense_type: 'all',
    search: '',
  });

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const fetchExpenses = useCallback(async () => {
    if (!isAuthorized) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.expense_type && filters.expense_type !== 'all') {
        params.append('expense_type', filters.expense_type);
      }
      params.append('status', 'approved');
      const r = await axios.get(`${API}/general-expenses?${params.toString()}`, {
        headers: authHeaders(),
      });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('[expense-aggregator] fetch failed:', e);
      setError(e.response?.data?.detail || e.message || 'فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [filters, isAuthorized, authHeaders]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  // Client-side search filter (lighter than re-fetching)
  const filteredRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.description, row.vendor, row.invoice_number, row.created_by_name, row.refund_operation_no]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, filters.search]);

  // KPIs
  const totals = useMemo(() => {
    const out = { count: filteredRows.length, total: 0, byType: {} };
    for (const r of filteredRows) {
      const amt = Number(r.amount || 0);
      out.total += amt;
      const k = r.expense_type || 'other_expense';
      out.byType[k] = (out.byType[k] || 0) + amt;
    }
    return out;
  }, [filteredRows]);

  const topTypes = useMemo(() => {
    const arr = Object.entries(totals.byType).map(([t, v]) => ({ type: t, amount: v }));
    arr.sort((a, b) => b.amount - a.amount);
    return arr.slice(0, 6);
  }, [totals]);

  const exportCsv = () => {
    if (filteredRows.length === 0) {
      alert('لا توجد بيانات للتصدير');
      return;
    }
    const headers = ['التاريخ', 'النوع', 'المبلغ', 'الوصف', 'المستفيد', 'الفاتورة/المرجع', 'وصل الاسترجاع', 'بواسطة'];
    const lines = [headers.join(',')];
    for (const r of filteredRows) {
      const date = r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : '';
      const row = [
        date,
        labelForType(r.expense_type).replace(/[↩️💸🛫🏪✈️📦🏢💼⚡📣🔸]/g, '').trim(),
        Number(r.amount || 0),
        (r.description || '').replace(/,/g, '؛'),
        (r.vendor || '').replace(/,/g, '؛'),
        r.invoice_number || '',
        r.refund_operation_no || '',
        (r.created_by_name || '').replace(/,/g, '؛'),
      ];
      lines.push(row.map((v) => `"${v}"`).join(','));
    }
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense_aggregator_${filters.start_date}_to_${filters.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAuthorized) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-600">
          🔒 مجمع التخفيضات متاح للمدير العام والمحاسب العام فقط.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" dir="rtl" data-testid="expense-aggregator-page">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <span>📊 مجمع التخفيضات والمصاريف</span>
            <div className="flex items-center gap-2">
              <Button onClick={fetchExpenses} size="sm" variant="outline" data-testid="ea-refresh-btn">
                🔄 تحديث
              </Button>
              <Button onClick={exportCsv} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ea-export-csv-btn">
                📥 تصدير CSV
              </Button>
            </div>
          </CardTitle>
          <p className="text-xs text-gray-600">
            كل المصاريف التي خصمت من الخزينة في الفترة المحددة — استرجاعات + سندات تخفيض + دفوعات + مصاريف عامة.
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-gray-50 rounded-md">
            <div>
              <Label className="text-xs">من تاريخ</Label>
              <Input type="date" value={filters.start_date}
                onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
                data-testid="ea-filter-start" />
            </div>
            <div>
              <Label className="text-xs">إلى تاريخ</Label>
              <Input type="date" value={filters.end_date}
                onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
                data-testid="ea-filter-end" />
            </div>
            <div>
              <Label className="text-xs">النوع</Label>
              <select
                value={filters.expense_type}
                onChange={(e) => setFilters((f) => ({ ...f, expense_type: e.target.value }))}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                data-testid="ea-filter-type"
              >
                <option value="all">كل الأنواع</option>
                {Object.entries(EXPENSE_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">بحث (الوصف، المستفيد، الفاتورة…)</Label>
              <Input
                placeholder="ابحث…"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                data-testid="ea-filter-search"
              />
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="إجمالي المصاريف" value={`${fmt(totals.total)} دج`}
              tone="rose" testid="ea-kpi-total" />
            <KpiCard label="عدد العمليات" value={totals.count}
              tone="blue" testid="ea-kpi-count" />
            <KpiCard label="استرجاعات" value={`${fmt(totals.byType.refund || 0)} دج`}
              tone="orange" testid="ea-kpi-refunds" />
            <KpiCard label="سندات تخفيض" value={`${fmt(totals.byType.discount_granted || 0)} دج`}
              tone="amber" testid="ea-kpi-discounts" />
          </div>

          {/* Breakdown by type */}
          {topTypes.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 text-gray-700">📈 توزيع حسب النوع</h4>
              <div className="space-y-1.5">
                {topTypes.map(({ type, amount }) => {
                  const pct = totals.total > 0 ? (amount / totals.total) * 100 : 0;
                  return (
                    <div key={type} className="flex items-center gap-2 text-xs">
                      <span className="w-40 shrink-0 text-gray-700">{labelForType(type)}</span>
                      <div className="flex-1 bg-gray-100 rounded h-3 overflow-hidden">
                        <div className="h-full bg-rose-400" style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="w-32 text-right font-semibold text-gray-800">
                        {fmt(amount)} دج ({pct.toFixed(1)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Table */}
          {loading && <div className="text-center text-gray-500 py-6 text-sm">جاري التحميل…</div>}
          {error && (
            <div className="text-center text-red-600 py-6 text-sm">{error}</div>
          )}
          {!loading && !error && (
            <div className="overflow-x-auto" data-testid="ea-table-wrap">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="border p-2 text-right">التاريخ</th>
                    <th className="border p-2 text-right">النوع</th>
                    <th className="border p-2 text-right">المبلغ</th>
                    <th className="border p-2 text-right">الوصف</th>
                    <th className="border p-2 text-right">المستفيد</th>
                    <th className="border p-2 text-right">المرجع</th>
                    <th className="border p-2 text-right">بواسطة</th>
                    <th className="border p-2 text-right">الوثيقة</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.id} className="hover:bg-rose-50/30" data-testid={`ea-row-${r.id}`}>
                      <td className="border p-2 whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="border p-2">
                        <Badge className="bg-rose-100 text-rose-900 border border-rose-200">
                          {labelForType(r.expense_type)}
                        </Badge>
                      </td>
                      <td className="border p-2 font-semibold text-rose-700 whitespace-nowrap">
                        -{fmt(r.amount)} دج
                      </td>
                      <td className="border p-2 max-w-xs">{r.description || '—'}</td>
                      <td className="border p-2">{r.vendor || '—'}</td>
                      <td className="border p-2 text-[11px] text-gray-600">
                        {r.refund_operation_no || r.invoice_number || '—'}
                      </td>
                      <td className="border p-2 text-[11px]">{r.created_by_name || '—'}</td>
                      <td className="border p-2 text-center">
                        {r.document_image_url ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api${r.document_image_url}`}
                            target="_blank" rel="noreferrer"
                            className="text-blue-700 hover:underline"
                          >📎</a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="border p-6 text-center text-gray-500">
                        ✨ لا توجد مصاريف في هذه الفترة
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredRows.length > 0 && (
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="border p-2 text-right" colSpan={2}>المجموع</td>
                      <td className="border p-2 text-rose-700 whitespace-nowrap">-{fmt(totals.total)} دج</td>
                      <td className="border p-2" colSpan={5}>
                        ({totals.count} عملية)
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KpiCard = ({ label, value, tone, testid }) => {
  const toneClasses = {
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
  };
  return (
    <div className={`rounded-md border p-3 ${toneClasses[tone] || toneClasses.blue}`} data-testid={testid}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </div>
  );
};

export default ExpenseAggregator;
